import { analytics } from "services/analytics";
import type { PlgProfile } from "plg/auth/plgTokenStore";
import { getPlgProfile } from "plg/auth/plgTokenStore";
import { clearPlgAnalyticsSession } from "plg/analytics/plgAnalyticsSession";
import { emailDomain, industrySlug, sizeBandSlug, urgencySignupSlug } from "plg/analytics/slugs";
import type { SignupAnswers } from "plg/types";
import type { PlgTier } from "plg/analytics/events";

/**
 * The portal's rule (/plg/me tier). full/redacted come from isVerified, which every token refresh
 * renews; a stored tier could be older. Only "blocked" is taken from /plg/me.
 */
export const plgTier = (profile: Pick<PlgProfile, "isVerified" | "tier"> | null): PlgTier =>
  profile?.tier === "blocked" ? "blocked" : profile?.isVerified ? "full" : "redacted";

export const currentPlgTier = () => plgTier(getPlgProfile());

const answered = (value: unknown) => (typeof value === "string" && value.trim() ? value.trim() : undefined);

/**
 * The business name, phone (as typed) and size from the first setup step (product 2026-10-28), for its
 * onboarding_step_completed and the account traits. Only the ones answered.
 */
export function companyTraits(answers: SignupAnswers): { company_name?: string; phone?: string; size_band?: string } {
  const traits = { company_name: answered(answers.companyName), phone: answered(answers.phone), size_band: sizeBandSlug(answered(answers.size)) };
  return Object.fromEntries(Object.entries(traits).filter(([, v]) => v !== undefined));
}

/** Account traits only the sign-up answers know (sent once, when the account is created). */
export function setupTraits(answers: SignupAnswers): Record<string, unknown> {
  const timeline = typeof answers.hiringTimeline === "string" ? answers.hiringTimeline : undefined;
  return {
    ...companyTraits(answers),
    urgency_signup: urgencySignupSlug(timeline),
    tools_selected: Array.isArray(answers.tools) ? answers.tools : [],
  };
}

/**
 * PLG people are identified by their email (product 2026-10-28), as the portal identifies its users, so
 * the sign-in identify and the account's later ones are one person. The plgCustomerId stays the account.
 */
const normalEmail = (email: unknown) => (typeof email === "string" ? email.trim().toLowerCase() : "");
const plgUserId = (profile: PlgProfile) => normalEmail(profile.email) || profile.plgCustomerId;

/**
 * Identifies the person once Google / Microsoft or a clicked magic link has verified their email: the
 * portal sends `signedIn` with the auth/start or magic-link verify answer, refused ones included.
 */
export function identifySignedIn(payload: unknown): void {
  const p = payload as { signedIn?: unknown; error?: Array<{ signedIn?: unknown }> } | null;
  const signedIn = (p?.signedIn ?? p?.error?.[0]?.signedIn) as { email?: unknown; ip?: unknown } | undefined;
  const email = normalEmail(signedIn?.email);
  if (!email) return;
  analytics.identify(email, { email, ...(typeof signedIn?.ip === "string" ? { ip: signedIn.ip } : {}) });
}

/**
 * One PLG login per company, so the account is the PLG customer: its traits go on the person and on the
 * account group (tracking sheet "Account"), keyed by the plgCustomerId.
 */
export function identifyPlg(profile: PlgProfile, extra: Record<string, unknown> = {}): void {
  const traits = {
    ...extra,
    ...(normalEmail(profile.email) ? { email: normalEmail(profile.email) } : {}),
    account_id: profile.plgCustomerId,
    customer_type: "plg",
    email_domain: emailDomain(profile.email),
    ...(answered(profile.companyName) ? { company_name: answered(profile.companyName) } : {}),
    ...(profile.emailType ? { email_type: profile.emailType } : {}),
    tier: plgTier(profile),
    industry: industrySlug(profile.industry),
    // At sign-up the profile has no size yet (it comes with /plg/me): the first step's answer stands in.
    size_band: sizeBandSlug(profile.size) ?? extra.size_band,
    state: profile.state,
  };
  analytics.identify(plgUserId(profile), traits);
  analytics.group(profile.plgCustomerId, traits);
}

/** Account traits learned after sign-up (e.g. has_voip from the meeting request), on the user and the account group. */
export function setPlgTraits(traits: Record<string, unknown>): void {
  const profile = getPlgProfile();
  if (!profile) return;
  analytics.identify(plgUserId(profile), traits);
  analytics.group(profile.plgCustomerId, traits);
}

/** On sign-out: the next person in this browser starts anonymous. */
export function resetPlgAnalytics(): void {
  clearPlgAnalyticsSession();
  analytics.reset();
}
